import React, { FC, memo, useCallback, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import ContainerWithLabel from '../../../../components/ContainerWithLabel';
import { InputField } from '../../../../components/InputField';
import Space, { Align, DirectionEnum, SpaceSizesEnum } from '../../../../components/Space/Space';
import { SocialButton } from '../../../../components/Buttons';
import { AlertType } from '../../../../components/Alert';
import styled from 'styled-components';
import { StyledAlert } from '../SignInForm';
import * as yup from 'yup';
import { resetPassword } from '../../../../services/auth';

const StyledForm = styled(Form)`
  width: 270px;
`;

const DashedLine = styled.div`
  width: 340px;
  height: 0;
  border-top: 1px dashed #dfdfdf;
`;

const SubmitButton = styled(SocialButton)<{ disabled: boolean; onClick: Function }>`
  width: 270px;
  height: 45px;
  align-items: center;
`;

type ForgotPassFormState = {
  validationSchema: object;
  isLoading: boolean;
  error: Error | null;
  submitHandler: (values: { email: string }, actions) => void;
};

const validationSchema = yup.object().shape({
  email: yup
    .string()
    .required('Required')
    .email(),
});

const initState = {
  isLoading: false,
  error: null,
};

const useForgotPasswordForm = (onReset: (email: string) => void): ForgotPassFormState => {
  const [state, setState] = useState<{ isLoading: boolean; error: Error | null }>(initState);

  const submitHandler = useCallback(async ({ email }) => {
    setState({ isLoading: true, error: null });

    await resetPassword(email);

    onReset(email);

    setState({ isLoading: true, error: null });
  }, []);

  return { validationSchema, submitHandler, ...state };
};

type ForgotPasswordFormProps = {
  onReset: (email: string) => void;
};

const ForgotPasswordForm: FC<ForgotPasswordFormProps> = ({ onReset }) => {
  const { validationSchema, submitHandler, error, isLoading } = useForgotPasswordForm(onReset);

  return (
    <Formik initialValues={{ email: '' }} onSubmit={submitHandler} validationSchema={validationSchema}>
      {({ dirty, isValid, errors, values, handleSubmit, isSubmitting, status }) => (
        <>
          <StyledForm>
            {error && <StyledAlert type={AlertType.ERROR}>{error.message}</StyledAlert>}
            <Space direction={DirectionEnum.vertical} size={SpaceSizesEnum.large} startWith={10} />
            <ContainerWithLabel label="Email" isBlack hasError={Boolean(errors.email)}>
              <Field name="email" component={InputField} wrapperClass="full" showError={Boolean(errors.email)} />
            </ContainerWithLabel>
          </StyledForm>
          <Space
            direction={DirectionEnum.vertical}
            size={SpaceSizesEnum.large}
            startWith={SpaceSizesEnum.large}
            align={Align.center}
          >
            <DashedLine />
            <SubmitButton
              disabled={!isValid || !dirty || isLoading}
              text={'Submit'}
              onClick={handleSubmit}
              isLoading={isLoading}
            />
          </Space>
        </>
      )}
    </Formik>
  );
};

export default memo(ForgotPasswordForm);
